import React, { useState } from "react";
import classes from "./submitPerformancePage.module.css";
import {
  ArrowLeft,
  Upload,
  LightbulbIcon,
  X,
  Video,
  Camera,
  Mic,
  Theater,
  Music,
  Heart,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { participateInArena } from "../../../redux/slices/andArenaSlice";
import Cookies from "js-cookie";
import { participateArenaUrl } from "../../../services";

const SubmitPerformancePage = ({
  categoryName,
  contestId,
  setSubmitPerformanceOpen,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTipsModalOpen, setIsTipsModalOpen] = useState(false);
  const [performanceDescription, setPerformanceDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [performanceTitle, setPerformanceTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("singing");
  const [modalDescription, setModalDescription] = useState("");
  const [activeTipCategory, setActiveTipCategory] = useState("general");

  const token = Cookies.get("accessToken");

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const openTipsModal = () => setIsTipsModalOpen(true);
  const closeTipsModal = () => setIsTipsModalOpen(false);

  const dispatch = useDispatch();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleSubmitPerformance = async () => {
    if (!performanceDescription || !selectedFile) {
      alert("Please provide a description and upload a file.");
      return;
    }

    try {
      setIsSubmitting(true);

      // Create FormData object
      const formData = new FormData();
      formData.append("contest_id", contestId || "ARENA-photography-2025-W14");
      formData.append("performance", selectedFile);
      formData.append("description", performanceDescription);
      formData.append("title", performanceTitle || "Performance");
      // formData.append("category", selectedCategory);

      const response = await fetch(participateArenaUrl, {
        method: "POST",
        body: formData,
        headers: {
          authorization: `Bearer ${token}`,
        },
      });

      // Dispatch with FormData
      // await dispatch(participateInArena(formData));

      // Reset form after successful submission
      setPerformanceDescription("");
      setSelectedFile(null);
      setPerformanceTitle("");
      setIsSubmitting(false);
      alert("Performance submitted successfully!");
    } catch (error) {
      setIsSubmitting(false);
      alert("Failed to submit performance: " + error.message);
    }
  };

  const handleGoBack = () => {
    setSubmitPerformanceOpen(false);
  };

  const handleUpload = (e) => {
    e.preventDefault();

    if (!selectedFile || !performanceTitle || !modalDescription) {
      alert("Please fill in all required fields and select a file.");
      return;
    }

    // Close modal and update the main form with the modal data
    setPerformanceDescription(modalDescription);
    setPerformanceTitle(performanceTitle);
    // We keep the selectedFile as is since it's already set
    closeModal();
  };

  // Performance tips content organized by categories
  const performanceTips = {
    general: {
      icon: <Heart size={28} />,
      title: "General Contest Tips",
      tips: [
        "Stand out from the crowd by showcasing your unique style and personality",
        "Follow all contest guidelines closely to avoid disqualification",
        "Submit your entry well before the deadline to avoid technical issues",
        "Ensure your submission quality is the best it can be - test your audio/video",
        "Include a compelling description that tells your story as an artist",
        "Engage with the platform community to build your audience",
      ],
    },
    video: {
      icon: <Video size={28} />,
      title: "Video Performance Tips",
      tips: [
        "Film in a well-lit area with minimal background distractions",
        "Position your camera at eye level for the most flattering angle",
        "Ensure good audio quality - consider using an external microphone",
        "Keep the camera steady using a tripod or stable surface",
        "Record multiple takes and choose the best one",
        "Edit your video to trim unnecessary parts and maintain viewer engagement",
        "Add titles or captions if they enhance your performance",
      ],
    },
    photography: {
      icon: <Camera size={28} />,
      title: "Photography Tips",
      tips: [
        "Follow the rule of thirds for balanced, appealing compositions",
        "Pay attention to lighting - natural light often works best",
        "Ensure your subject is in sharp focus",
        "Consider the story your image tells - what emotion does it convey?",
        "Edit thoughtfully to enhance but not oversaturate your image",
        "Choose images that demonstrate your technical skill and creative vision",
        "Submit high-resolution files that showcase the details of your work",
      ],
    },
    audio: {
      icon: <Mic size={28} />,
      title: "Audio Performance Tips",
      tips: [
        "Record in a quiet environment with good acoustics",
        "Use a quality microphone positioned correctly for your voice/instrument",
        "Consider using a pop filter for vocal recordings",
        "Monitor your levels to avoid distortion or clipping",
        "Apply subtle EQ and compression to polish your sound",
        "Include a brief intro and outro to frame your performance",
        "Make sure your performance stays within the time limits",
      ],
    },
    presentation: {
      icon: <Theater size={28} />,
      title: "Presentation Tips",
      tips: [
        "Dress appropriately for your performance style and genre",
        "Practice until you feel confident and natural on camera",
        "Show enthusiasm and energy that translates through the screen",
        "Make eye contact with the camera to connect with viewers",
        "Consider your body language and posture throughout",
        "Speak clearly and at an appropriate pace if narrating or singing",
        "Be authentic - your genuine passion will resonate with judges and viewers",
      ],
    },
    portfolio: {
      icon: <Music size={28} />,
      title: "Building Your Portfolio",
      tips: [
        "Use this contest submission as portfolio content for future opportunities",
        "Create a consistent artistic identity across all your submissions",
        "Showcase versatility while maintaining your unique style",
        "Document your creative process to share alongside finished work",
        "Consider how each submission fits into your broader body of work",
        "Request feedback from peers to continually improve your craft",
        "Network with other artists on the platform for collaboration opportunities",
      ],
    },
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h1 className={classes.headerTitle}>
          Here's your time to shine. Get a chance to be elected as a winner on
          the AND Stage
        </h1>
        <button className={classes.backButton} onClick={handleGoBack}>
          <ArrowLeft size={20} />
          <span>Go back</span>
        </button>
      </div>

      <div className={classes.rulesContainer}>
        <h2 className={classes.rulesTitle}>
          <span className={classes.rulesHighlight}>Rules</span> for artists
          competing this week
        </h2>

        <ol className={classes.rulesList}>
          <li className={classes.rule}>
            <strong>No Lip-Syncing</strong> – Live or pre-recorded vocals only,
            no miming! Backing Tracks Allowed – But the main vocals must be
            yours.
          </li>
          <li className={classes.rule}>
            <strong>Originality Matters</strong> – Your performance must be
            original or have a unique spin on existing work. Plagiarism will
            result in disqualification.
          </li>
          <li className={classes.rule}>
            <strong>Respect the Stage</strong> – No offensive, discriminatory,
            or inappropriate content. Keep it classy, keep it creative.
          </li>
          <li className={classes.rule}>
            <strong>Time Limit</strong> – Each performance must be within the
            allocated time (e.g., 2-5 minutes).
          </li>
          <li className={classes.rule}>
            <strong>Shot, One Chance</strong> – Once submitted, no re-uploading
            or modifications. Perform your best!
          </li>
          <li className={classes.rule}>
            {" "}
            Your performance Video will be uploaded to AND YouTube Channel which
            will be embedded next to your voting Card.
          </li>
        </ol>

        <div className={classes.divider}></div>

        <div className={classes.actionCards}>
          <div className={classes.actionCard} onClick={openModal}>
            <div className={classes.actionCardIcon}>
              <Upload size={40} />
            </div>
            <h3 className={classes.actionCardTitle}>Submit my Performance</h3>
          </div>

          <div className={classes.actionCard} onClick={openTipsModal}>
            <div className={classes.actionCardIcon}>
              <LightbulbIcon size={40} />
            </div>
            <h3 className={classes.actionCardTitle}>Performance Tips</h3>
          </div>

          <div className={classes.submitSection}>
            <textarea
              className={classes.descriptionInput}
              placeholder="Tell us something about your performance."
              value={performanceDescription}
              onChange={(e) => setPerformanceDescription(e.target.value)}
            ></textarea>
            <button
              className={classes.submitButton}
              onClick={handleSubmitPerformance}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Uploading..." : "Upload for Review"}
            </button>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {isModalOpen && (
        <div className={classes.modalOverlay}>
          <div className={classes.modalContent}>
            <div className={classes.modalHeader}>
              <h2 className={classes.modalTitle}>Upload Your Performance</h2>
              <button className={classes.closeButton} onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleUpload} className={classes.uploadForm}>
              <div className={classes.fileInputContainer}>
                <label
                  htmlFor="performanceVideo"
                  className={classes.fileInputLabel}
                >
                  <Upload size={32} />
                  <span>
                    {selectedFile
                      ? selectedFile.name
                      : "Drag and drop your video file here, or click to browse"}
                  </span>
                </label>
                <input
                  type="file"
                  id="performanceVideo"
                  accept="video/*,image/*"
                  className={classes.fileInput}
                  onChange={handleFileChange}
                />
              </div>

              <div className={classes.formGroup}>
                <label htmlFor="performanceTitle" className={classes.label}>
                  Performance Title
                </label>
                <input
                  type="text"
                  id="performanceTitle"
                  className={classes.textInput}
                  value={performanceTitle}
                  onChange={(e) => setPerformanceTitle(e.target.value)}
                  required
                />
              </div>

              <div className={classes.formGroup}>
                <label
                  htmlFor="performanceDescription"
                  className={classes.label}
                >
                  Description
                </label>
                <textarea
                  id="performanceDescription"
                  className={classes.textArea}
                  rows="4"
                  placeholder="Tell us about your performance..."
                  value={modalDescription}
                  onChange={(e) => setModalDescription(e.target.value)}
                  required
                ></textarea>
              </div>

              <div className={classes.formGroup}>
                <label htmlFor="performanceCategory" className={classes.label}>
                  Category
                </label>
                <select
                  id="performanceCategory"
                  className={classes.select}
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="singing">Singing</option>
                  <option value="dancing">Dancing</option>
                  <option value="instrumental">Instrumental</option>
                  <option value="comedy">Comedy</option>
                  <option value="spoken-word">Spoken Word</option>
                  <option value="photography">Photography</option>
                </select>
              </div>

              <div className={classes.modalActions}>
                <button
                  type="button"
                  className={classes.cancelButton}
                  onClick={closeModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button type="submit" className={classes.uploadButton}>
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Performance Tips Modal */}
      {isTipsModalOpen && (
        <div className={classes.modalOverlay}>
          <div
            className={`${classes.modalContent} ${classes.tipsModalContent}`}
          >
            <div className={classes.modalHeader}>
              <h2 className={classes.modalTitle}>Performance Tips</h2>
              <button className={classes.closeButton} onClick={closeTipsModal}>
                <X size={24} />
              </button>
            </div>

            <div className={classes.tipsContainer}>
              <div className={classes.tipsCategories}>
                {Object.keys(performanceTips).map((category) => (
                  <button
                    key={category}
                    className={`${classes.tipCategoryButton} ${
                      activeTipCategory === category
                        ? classes.activeTipCategory
                        : ""
                    }`}
                    onClick={() => setActiveTipCategory(category)}
                  >
                    <span className={classes.tipCategoryIcon}>
                      {performanceTips[category].icon}
                    </span>
                    <span className={classes.tipCategoryText}>
                      {performanceTips[category].title}
                    </span>
                  </button>
                ))}
              </div>

              <div className={classes.tipsContent}>
                <h3 className={classes.tipsContentTitle}>
                  {performanceTips[activeTipCategory].title}
                </h3>
                <ul className={classes.tipsList}>
                  {performanceTips[activeTipCategory].tips.map((tip, index) => (
                    <li key={index} className={classes.tipItem}>
                      {tip}
                    </li>
                  ))}
                </ul>

                <div className={classes.tipsFooter}>
                  <p className={classes.tipsFooterText}>
                    Remember: Authenticity and passion will set you apart! Good
                    luck with your submission.
                  </p>
                </div>
              </div>
            </div>

            <div className={classes.modalActions}>
              <button
                className={classes.tipsPrimaryButton}
                onClick={closeTipsModal}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmitPerformancePage;
